import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import _ from 'lodash';
import { supabase } from '../lib/supabase';

import { Employee } from '../types/employee';
import { fetchEmployees } from '../utils/api';
import { calculateMapCenter } from '../utils/geoUtils';

interface EmployeeContextProps {
  employees: Employee[];
  filteredEmployees: Employee[];
  isLoading: boolean;
  error: string | null;
  centerCoordinates: [number, number];
  getEmployeeById: (id: string) => Employee | undefined;
  searchEmployees: (term: string) => void;
  reloadEmployees: () => Promise<void>;
}

const EmployeeContext = createContext<EmployeeContextProps | undefined>(undefined);

export const EmployeeProvider = ({ children }: { children: ReactNode }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [employeesById, setEmployeesById] = useState<Record<string, Employee>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [centerCoordinates, setCenterCoordinates] = useState<[number, number]>([20, 0]);
  const [searchTerm, setSearchTerm] = useState('');

  const loadEmployees = async () => {
    try {
      setIsLoading(true);
      const data = await fetchEmployees();
      setEmployees(data);
      setEmployeesById(_.fromPairs(_.map(data, e => [e.id, e])));

      const term = searchTerm.trim().toLowerCase();
      if (term) {
        setFilteredEmployees(data.filter(e =>
          e.name.toLowerCase().includes(term) ||
          e.role.toLowerCase().includes(term) ||
          e.location.city.toLowerCase().includes(term) ||
          e.location.country.toLowerCase().includes(term) ||
          e.department.toLowerCase().includes(term) ||
          e.employer.toLowerCase().includes(term)
        ));
      } else {
        setFilteredEmployees(data);
      }

      if (data.length > 0) setCenterCoordinates(calculateMapCenter(data));
    } catch (err) {
      setError('Failed to load employee data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) loadEmployees();
      else setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        loadEmployees();
      } else {
        setEmployees([]);
        setFilteredEmployees([]);
        setEmployeesById({});
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const getEmployeeById = (id: string): Employee | undefined => employeesById[id];

  const searchEmployees = (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredEmployees(employees);
      return;
    }
    const lower = term.toLowerCase();
    setFilteredEmployees(employees.filter(e =>
      e.name.toLowerCase().includes(lower) ||
      e.role.toLowerCase().includes(lower) ||
      e.location.city.toLowerCase().includes(lower) ||
      e.location.country.toLowerCase().includes(lower) ||
      e.department.toLowerCase().includes(lower) ||
      e.employer.toLowerCase().includes(lower)
    ));
  };

  return (
    <EmployeeContext.Provider value={{
      employees,
      filteredEmployees,
      isLoading,
      error,
      centerCoordinates,
      getEmployeeById,
      searchEmployees,
      reloadEmployees: loadEmployees,
    }}>
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployee = (): EmployeeContextProps => {
  const context = useContext(EmployeeContext);
  if (context === undefined) throw new Error('useEmployee must be used within an EmployeeProvider');
  return context;
};
